<script lang="ts" setup>
  import { ref } from 'vue';
  import type { Song } from '../../../../shared/api/song.ts';
  import { startUserAction, userAction } from '../../../../shared/observability/userAction.ts';
  import SongDetails from './SongDetails.vue';

  const props = defineProps<{
    song: Song;
    label?: string;
  }>();

  const isMasked = ref(true);

  const toggleMask = () => {
    if (isMasked.value) startUserAction(userAction.roundRevealSong, { ...(props.label && { label: props.label }) });
    isMasked.value = !isMasked.value;
  };
</script>

<template>
  <div class="song-card-wrapper" @click="toggleMask">
    <p v-if="label" class="label">{{ label }}</p>
    <div :class="['song-card', { 'is-masked': isMasked }]">
      <div class="album-art">
        <template v-if="!isMasked">
          <img :alt="song.title" :src="song.albumImages[1]" />
        </template>
        <div v-else class="mask-placeholder-art">
          <div class="music-icon">🎵</div>
          <div class="tap-to-show">tap to show</div>
        </div>
      </div>
      <div class="song-info">
        <SongDetails v-if="!isMasked" :song="song" />
        <div v-else class="mask-placeholder-info">
          <div class="mask-line title-mask"></div>
          <div class="mask-line artist-mask"></div>
          <div class="mask-line year-mask"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .song-card-wrapper {
    align-items: center;
    background: var(--color-surface-translucent);
    border-radius: var(--radius-card);
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: var(--card-padding);
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;
    width: min(95%, 45dvh);
  }

  .song-card-wrapper:hover {
    box-shadow: 0 6px 16px var(--color-shadow-faint);
    transform: translateY(-2px);
  }

  .song-card-wrapper:active {
    transform: translateY(0);
  }

  .label {
    font-size: var(--font-size-sm);
    letter-spacing: 0.05em;
    margin: 0;
    text-transform: uppercase;
  }

  .song-card {
    background: var(--color-surface);
    border-radius: calc(var(--radius-card) - var(--card-padding));
    box-shadow: 0 4px 12px var(--color-shadow);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    width: 100%;
  }

  .album-art {
    aspect-ratio: 1;
    background: var(--color-placeholder);
    overflow: hidden;
    width: 100%;
  }

  .album-art img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  .song-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: var(--card-padding);
  }

  .is-masked {
    background: var(--color-surface-recessed);
    cursor: default;
    -webkit-user-select: none;
    user-select: none;
  }

  .mask-placeholder-art {
    align-items: center;
    animation: pulse 3s ease infinite;
    background: linear-gradient(
      135deg,
      var(--color-skeleton) 25%,
      var(--color-skeleton-highlight) 50%,
      var(--color-skeleton) 75%
    );
    background-size: 400% 400%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: 100%;
    justify-content: center;
    overflow: hidden;
    position: relative;
    width: 100%;
  }

  .music-icon {
    animation: float 3s ease-in-out infinite;
    filter: drop-shadow(0 0 15px var(--color-glow));
    font-size: var(--font-size-4xl);
    opacity: 0.4;
  }

  .tap-to-show {
    animation: bounce 2s ease-in-out infinite;
    backdrop-filter: blur(4px);
    background: var(--color-surface-frosted);
    border-radius: var(--radius-full);
    box-shadow: 0 2px 8px var(--color-shadow-faint);
    color: var(--color-text-muted);
    font-size: var(--font-size-xs);
    letter-spacing: 0.1em;
    padding: 0.25rem 0.6rem;
    text-transform: uppercase;
  }

  .mask-placeholder-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .mask-line {
    background: var(--color-skeleton);
    border-radius: var(--radius-sm);
    height: 1rem;
    overflow: hidden;
    position: relative;
  }

  .mask-line::after {
    animation: shimmer 1.5s infinite;
    background: linear-gradient(
      90deg,
      transparent,
      var(--color-shimmer),
      var(--color-shimmer-tint),
      var(--color-shimmer),
      transparent
    );
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  .title-mask {
    height: 1.3rem;
    width: 85%;
  }

  .artist-mask {
    width: 60%;
  }

  .year-mask {
    width: 30%;
  }

  @keyframes pulse {
    0% {
      background-position: 0% 50%;
    }

    50% {
      background-position: 100% 50%;
    }

    100% {
      background-position: 0% 50%;
    }
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }

    100% {
      transform: translateX(100%);
    }
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0) rotate(0deg);
    }

    50% {
      transform: translateY(-10px) rotate(5deg);
    }
  }

  @keyframes bounce {
    0%,
    100% {
      transform: scale(1);
    }

    50% {
      transform: scale(1.05);
    }
  }
</style>
